import { h } from 'preact';

import { confirm } from 'utils/modal';
import { parseVideoUrl } from 'utils/videoTitle';
import emptyImage from 'utils/emptyImage';

import MovieEditor from 'components/editor/movies/movieEditor';
import SelectableSection from 'components/editor/selectableSection';
import GoToMediaButton from 'components/media/goToMediaButton';

import s from 'components/editor/movies/videos/videos.sss';

export default class VideosEditor extends MovieEditor {
	s = s;

	constructor(props) {
		super(props);

		this.onAddVideoClick = this.onAddVideoClick.bind(this);
	}

	// ===============================
	// HELPERS
	// ===============================
	getVideos() {
		return Object.entries(this.movie?.trailers || {})
			.map(([key, value]) => ({
				...value,
				id: key,
				link: value.url || value.link || '', // Support both 'url' (from hero) and 'link' (legacy)
				order: value.order ?? 99,
			}))
			.filter(video => video.link && parseVideoUrl(video.link)) // Only include videos with valid links
			.sort((a, b) => a.order - b.order) // Sort by order
			.slice(0, 3); // Limit to 3 videos for display
	}

	async onAddVideoClick() {
		const ok = await confirm('Videos can be edited from the movie page media tab.', {
			title: 'Add video',
			button: 'Go to media',
			buttonIconAlt: 'chevron-right',
		});
		if (!ok) return;
		
		this.navigateToMedia();
	}

	getThumbnailUrl(link) {
		const videoData = parseVideoUrl(link);
		if (!videoData) return emptyImage;

		// If URL has a video ID (even with playlist), use video thumbnail
		if (videoData.youtubeId) {
			return `https://img.youtube.com/vi/${videoData.youtubeId}/mqdefault.jpg`;
		}

		// For pure playlist URLs (no video ID), use playlist thumbnail
		// Note: YouTube doesn't provide direct playlist thumbnails via img.youtube.com
		// Using a generic placeholder for now
		if (videoData.youtubePlaylistId) {
			// Option: Could use YouTube API or oEmbed, but for simplicity using placeholder
			// Future enhancement: Fetch first video from playlist and use its thumbnail
			return emptyImage; // Generic playlist icon
		}

		// For Vimeo, we'll use empty image as getting thumbnail requires API call
		return emptyImage;
	}

	// ===============================
	// VISUAL
	// ===============================
	renderVisual() {
		const pageTitle = this.text(this.state.thundrData?.pages?.['boltWatchMore']?.data?.title || '$WATCH_MORE', 'Watch More');
		const videos = this.getVideos();
		const hasVideos = videos.length > 0;
		const showViewAll = Object.keys(this.movie?.trailers || {}).length > 3; // Show "View All" if more than 3 videos exist
		
		return (
			<SelectableSection class={this.s.wrapper} section="/pages/videos" {...this.props}>
				<div class={this.s.videosContainer}>
					<h2 class={this.s.videosHeader}>{pageTitle}</h2>
					<div class={this.s.videoDisplay}>
						<div class={this.s.videoGrid}>
							{videos.map((video) => (
								<div key={video.id} class={this.s.videoTile}>
									<div class={this.s.thumbnailWrapper}>
										<img
											src={this.getThumbnailUrl(video.link)}
											alt={video.thumbnailAlt || video.defaultTitle}
											class={this.s.thumbnail}
										/>
									</div>
									<div class={this.s.videoTileTitle}>
										<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class={this.s.playIcon}>
											<path fill-rule="evenodd" clip-rule="evenodd" d="M3.51771 1C3.61221 1 3.71539 1.0251 3.82472 1.07752L3.936 1.13913L14.6143 7.412C14.8714 7.57387 15 7.7865 15 8C15 8.183 14.9055 8.366 14.7166 8.51594L14.6143 8.588L3.936 14.8609C3.786 14.9554 3.64371 15 3.51771 15C3.23875 15 3.03913 14.7831 3.00514 14.4018L3 14.2825V1.7175C3 1.26337 3.21086 1 3.51771 1ZM4 2.336V13.663L13.641 8L4 2.336Z" fill="#1748F6" />
										</svg>
										<span>{video.title || video.defaultTitle || 'Untitled Video'}</span>
									</div>
								</div>
							))}
							{!hasVideos && (
								<div data-section="#" class={this.s.emptyStateTile} onClick={this.onAddVideoClick}>
									<div class={this.s.emptyStateThumbnail}>
										<span class={this.s.addVideoLabel}>+ Add video</span>
									</div>
								</div>
							)}
						</div>
						{showViewAll && (
							<div class={this.s.viewAllButtonContainer}>
								<button class={this.s.viewAllButton}>
								View All
								</button>
							</div>
						)}
					</div>
				</div>
			</SelectableSection>
		);
	}

	// ===============================
	// SETTINGS SIDEBAR
	// ===============================
	renderSettings() {
		return (
			<div class={this.s.settings}>
				{this.renderSettingsNavigation([{ label: 'Videos', path: '/pages/videos' }])}
				<span> Videos can be edited from the movie page media tab.</span>
				<GoToMediaButton dark movie={this.movie} />
			</div>
		);
	}
}
